import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { AdministratorStatisticsService } from 'src/app/services/administrator-statistics.service';
import { first } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-risk-trends-graph',
    templateUrl: './risk-trends-graph.component.html',
    styleUrls: ['./risk-trends-graph.component.scss']
})
export class RiskTrendsGraphComponent implements OnChanges {
    @Input() year: number;
    @Input() submissionType: string;
    userInterfaceDisplay: string;
    originalityRiskFactorHighRisk: number;
    originalityRiskFactorMediumRisk: number;
    institutionSettings: any;
    user: any;

    chartOptions: any = {
        series: [
            { name: '80% - 100%', data: Array(12).fill(0) },
            { name: '20% - 80%', data: Array(12).fill(0) },
            { name: '0% - 20%', data: Array(12).fill(0) }
        ],
        chart: {
            type: 'area',
            height: 320,
            width: '100%',
            toolbar: { show: true },
            zoom: { enabled: true }
        },
        colors: ['#E74C3C', '#F4B400', '#43B77A'],
        stroke: {
            width: 3,
            curve: 'smooth'
        },
        dataLabels: { enabled: false },
        xaxis: {
            categories: [
                'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
            ],
            labels: { style: { fontSize: '13px', colors: '#888' } }
        },
        yaxis: {
            min: 0,
            max: 100,
            tickAmount: 6,
            labels: { show: true, style: { fontSize: '13px', colors: '#888' }, formatter: (val) => val.toFixed(0) }
        },
        legend: { show: true, position: 'bottom', horizontalAlign: 'center', fontSize: '13px' },
        tooltip: { y: { formatter: (val) => val } },
        grid: {
            borderColor: '#e5e7eb',
            row: { colors: ['#fff', 'transparent'], opacity: 0.5 },
            xaxis: { lines: { show: true } },
            yaxis: { lines: { show: true } }
        },
        fill: { type: 'solid', colors: ['#E74C3C', '#F4B400', '#43B77A'] },
        responsive: [
            { breakpoint: 700, options: { chart: { height: 200 } } }
        ]
    };
    isLoading = false;
    errorMessage = '';

    constructor(private statistics: AdministratorStatisticsService, private translate: TranslateService) { }

    ngOnInit(): void {
        this.user = JSON.parse(localStorage.getItem('user'));
        this.institutionSettings = this.user?.Institution?.InstitutionSettings?.[0];
        this.userInterfaceDisplay = this.institutionSettings?.userInterfaceDisplay;
        this.originalityRiskFactorHighRisk = this.institutionSettings?.originalityRiskFactorHighRisk;
        this.originalityRiskFactorMediumRisk = this.institutionSettings?.originalityRiskFactorMediumRisk;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.year && this.submissionType) {
            this.fetchRiskTrends(this.year, this.submissionType);
        }
    }

    fetchRiskTrends(year: number, type: string = 'student') {
        this.isLoading = true;
        this.errorMessage = '';
        let obs;
        if (type === 'student') {
            obs = this.statistics.institutionSubmissionsStatistics(year);
        } else {
            obs = this.statistics.institutionSubmissionsStatisticsBasic(year);
        }

        obs.pipe(first()).subscribe(
            (data) => {
                console.log('data', data);

                // Calculate dynamic Y-axis maximum based on data for better visualization of small datasets
                const allValues = [
                    ...(data.highSimilarity || []),
                    ...(data.mediumSimilarity || []),
                    ...(data.lowSimilarity || [])
                ];
                const maxValue = Math.max(...allValues, 0);

                // Aggressive scaling: make graphs always look filled with minimal empty space
                let yAxisMax;
                if (maxValue <= 5) {
                    yAxisMax = Math.max(maxValue + 2, 8); // Very tight scaling for tiny data
                } else if (maxValue <= 15) {
                    yAxisMax = Math.max(maxValue + 3, 18); // Tight scaling for small data
                } else if (maxValue <= 50) {
                    yAxisMax = Math.ceil(maxValue * 1.15); // Minimal padding for medium values
                } else {
                    yAxisMax = Math.ceil(maxValue * 1.1); // Very minimal padding for large values
                }

                // const yAxisMax = Math.max(Math.ceil(maxValue * 1.2), 100);


                this.chartOptions = {
                    series: [
                        {
                            name: this.getHighRiskLabel2(),
                            data: data.highSimilarity || Array(12).fill(0)
                        },
                        {
                            name: this.getMediumRiskLabel2(),
                            data: data.mediumSimilarity || Array(12).fill(0)
                        },
                        {
                            name: this.getLowRiskLabel2(),
                            data: data.lowSimilarity || Array(12).fill(0)
                        }
                    ],
                    chart: {
                        type: 'area',
                        height: '500',
                        width: '100%',
                        toolbar: { show: true },
                        zoom: { enabled: true }
                    },
                    colors: ['#E74C3C', '#F4B400', '#43B77A'],
                    stroke: {
                        width: 3,
                        curve: 'smooth'
                    },
                    dataLabels: {
                        enabled: false
                    },
                    xaxis: {
                        categories: [
                            'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                            'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
                        ],
                        labels: {
                            style: { fontSize: '13px', colors: '#888' },
                            axisBorder: { show: true, color: '#1a237e' },
                            axisTicks: { show: false },
                            crosshairs: { show: false },
                        }
                    },
                    yaxis: {
                        min: 0,
                        max: yAxisMax,
                        tickAmount: 6,
                        labels: {
                            show: true,
                            style: { fontSize: '13px', colors: '#888' },
                            formatter: (val) => val.toFixed(0)
                        }
                    },
                    legend: {
                        show: true,
                        position: 'bottom',
                        horizontalAlign: 'center',
                        fontSize: '13px',
                        // markers: { radius: 12 }
                    },
                    tooltip: {
                        y: { formatter: (val) => val }
                    },
                    grid: {
                        borderColor: '#e5e7eb',
                        row: { colors: ['#fff', 'transparent'], opacity: 0.5 },
                        xaxis: {
                            lines: { show: true }
                        },
                        yaxis: {
                            lines: { show: true }
                        }
                    },
                    fill: {
                        type: 'solid',
                        colors: ['#E74C3C', '#F4B400', '#43B77A'] // Match your line colors
                    },
                    responsive: [
                        {
                            breakpoint: 700,
                            options: { chart: { height: 200 } }
                        }
                    ]
                };
                this.isLoading = false;
            },
            (error) => {
                this.chartOptions = null;
                this.errorMessage = 'Failed to load chart data.';
                this.isLoading = false;
            }
        );
    }

    getHighRiskLabel(): string {
        if (this.userInterfaceDisplay === 'riskFactor') {
            return this.translate.instant('new_entries.high_risk_submissions');
        } else {
            return `${this.originalityRiskFactorHighRisk}% - 100%`;
        }
    }

    getMediumRiskLabel(): string {
        if (this.userInterfaceDisplay === 'riskFactor') {
            return this.translate.instant('new_entries.medium_risk_submissions');
        } else {
            return `${this.originalityRiskFactorMediumRisk}% - ${this.originalityRiskFactorHighRisk}%`;
        }
    }

    getLowRiskLabel(): string {
        if (this.userInterfaceDisplay === 'riskFactor') {
            return this.translate.instant('new_entries.low_risk_submissions');
        } else {
            return this.translate.instant('new_entries.submission_from') + ` 0% - ${this.originalityRiskFactorMediumRisk}%`;
        }
    }

    getHighRiskLabel2(): string {
        if (this.userInterfaceDisplay === 'riskFactor') {
            return this.translate.instant('new_entries.high_risk');
        } else {
            return this.translate.instant('new_entries.submission_from') + ` ${this.originalityRiskFactorHighRisk}% - 100%`;
        }
    }

    getMediumRiskLabel2(): string {
        if (this.userInterfaceDisplay === 'riskFactor') {
            return this.translate.instant('new_entries.medium_risk');
        } else {
            return this.translate.instant('new_entries.submission_from') + ` ${this.originalityRiskFactorMediumRisk}% - ${this.originalityRiskFactorHighRisk}%`;
        }
    }

    getLowRiskLabel2(): string {
        if (this.userInterfaceDisplay === 'riskFactor') {
            return this.translate.instant('new_entries.low_risk');
        } else {
            return this.translate.instant('new_entries.submission_from') + ` 0% - ${this.originalityRiskFactorMediumRisk}%`;
        }
    }
} 